import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { useCaseSearch } from '../../hooks/useWorkQueue';
import { type WorkQueueCase, type StatusMeta, fetchCase } from '../../services/workQueue';
import { useStatusCatalog } from '../../services/statusCatalog';
import { PHASE_HEX, phaseTint } from '../../utils/phaseColors';
import { formatRelative } from '../patient-files/timeline-rail/event-time';
import { useToast } from '../../providers/ToastProvider';
import { PracticeDoctorLabel } from '../shared/PracticeDoctorLabel';
import { readSearchHistory, removeSearchHistory, type SearchHistoryEntry } from './searchHistory';

const SEARCH_DEBOUNCE_MS = 300;
const AVATAR_SIZE_CLASS = 'w-7 h-7';

interface SearchDropdownProps {
    statusLookup: Map<string, StatusMeta>;
    onSelect: (result: WorkQueueCase) => void;
}

function PatientAvatar({ name, thumbnailUrl }: { name: string; thumbnailUrl: string | null }) {
    const [imgFailed, setImgFailed] = useState(false);
    const initials = name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('');

    if (thumbnailUrl && !imgFailed) {
        return (
            <img
                src={thumbnailUrl}
                alt={name}
                onError={() => setImgFailed(true)}
                className={`${AVATAR_SIZE_CLASS} rounded-full object-cover ring-1 ring-gray-200 flex-shrink-0`}
            />
        );
    }

    return (
        <div
            className={`${AVATAR_SIZE_CLASS} rounded-full bg-yellow-400 flex items-center justify-center text-xs font-bold text-gray-600 ring-1 ring-gray-200 flex-shrink-0`}
        >
            {initials}
        </div>
    );
}

interface ResultRowProps {
    result: WorkQueueCase;
    isHighlighted: boolean;
    statusLookup: Map<string, StatusMeta>;
    onMouseEnter: () => void;
    onSelect: () => void;
    rowRef?: React.RefObject<HTMLButtonElement>;
}

function ResultRow({
    result,
    isHighlighted,
    statusLookup,
    onMouseEnter,
    onSelect,
    rowRef
}: ResultRowProps) {
    const catalog = useStatusCatalog();
    const stepMeta = statusLookup.get(result.statusId);
    const stepLabel = catalog.display(result.statusId);
    const phaseColor = PHASE_HEX[stepMeta?.phase ?? result.phase];

    return (
        <button
            ref={rowRef}
            type="button"
            role="option"
            aria-selected={isHighlighted}
            onMouseEnter={onMouseEnter}
            onClick={onSelect}
            className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                isHighlighted ? 'bg-gray-100 shadow-[inset_2px_0_0_#1f2937]' : ''
            }`}
        >
            <PatientAvatar name={result.name} thumbnailUrl={result.profileThumbnailUrl} />
            <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-gray-800 truncate block">
                    {result.name}
                </span>
                <div className="flex items-center gap-2 min-w-0 mt-0.5">
                    <span
                        className={`text-xs font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 max-w-[140px] truncate ${
                            phaseColor ? '' : 'text-gray-500 bg-gray-100'
                        }`}
                        style={
                            phaseColor
                                ? { color: phaseColor, background: phaseTint(phaseColor, 0.1) }
                                : undefined
                        }
                        title={stepLabel}
                    >
                        {stepLabel}
                    </span>
                    <PracticeDoctorLabel
                        practiceName={result.practice}
                        doctorName={result.practiceDoctor}
                        format="practice-doctor"
                        className="text-xs truncate"
                        toneClassName="text-gray-500"
                    />
                </div>
            </div>
        </button>
    );
}

interface RecentRowProps {
    entry: SearchHistoryEntry;
    isLoading: boolean;
    onSelect: () => void;
}

function RecentRow({ entry, isLoading, onSelect }: RecentRowProps) {
    return (
        <button
            type="button"
            onClick={onSelect}
            disabled={isLoading}
            className="w-full flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-50 disabled:cursor-wait"
        >
            <PatientAvatar name={entry.name} thumbnailUrl={entry.profileThumbnailUrl} />
            <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-gray-800 truncate block">
                    {entry.name}
                </span>
                <PracticeDoctorLabel
                    practiceName={entry.practice}
                    format="practice-only"
                    className="text-xs truncate block mt-0.5"
                    toneClassName="text-gray-500"
                />
            </div>
            {isLoading ? (
                <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin flex-shrink-0" />
            ) : (
                <span className="text-[11px] text-gray-400 whitespace-nowrap flex-shrink-0">
                    {formatRelative(entry.selectedAt)}
                </span>
            )}
        </button>
    );
}

export function SearchDropdown({ statusLookup, onSelect }: SearchDropdownProps) {
    const [inputValue, setInputValue] = useState('');
    const [debouncedTerm, setDebouncedTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    // Enter pressed before the current term's first page arrived (debounce was
    // flushed / fetch in flight): select the first result as soon as it lands.
    const [pendingEnter, setPendingEnter] = useState(false);
    // History is loaded lazily on focus so it reflects the latest state.
    const [history, setHistory] = useState<SearchHistoryEntry[]>([]);
    // Tracks which history entry id is currently being fetched.
    const [loadingHistoryId, setLoadingHistoryId] = useState<string | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const scrollPanelRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const highlightedRowRef = useRef<HTMLButtonElement>(null);

    const { showToast } = useToast();

    useEffect(() => {
        const handle = setTimeout(() => {
            const trimmed = inputValue.trim();
            setDebouncedTerm(trimmed);
            setHighlightedIndex(0);
            setIsOpen(trimmed.length >= 2);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [inputValue]);

    const {
        results,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        isPlaceholderData
    } = useCaseSearch(debouncedTerm);

    // Keep the highlighted row scrolled into view within the panel.
    useEffect(() => {
        if (highlightedRowRef.current) {
            highlightedRowRef.current.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex]);

    // Infinite-scroll sentinel: fires fetchNextPage when the bottom of the list
    // enters the visible area of the scrollable panel.
    useEffect(() => {
        if (!isOpen || !hasNextPage || isFetchingNextPage) return;
        const node = sentinelRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    fetchNextPage();
                }
            },
            { root: scrollPanelRef.current, rootMargin: '60px 0px' }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [isOpen, hasNextPage, isFetchingNextPage, fetchNextPage, results.length]);

    useEffect(() => {
        const handleMouseDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, []);

    function handleSelect(result: WorkQueueCase) {
        setInputValue('');
        setDebouncedTerm('');
        setIsOpen(false);
        onSelect(result);
    }

    async function handleRecentSelect(entry: SearchHistoryEntry) {
        if (loadingHistoryId !== null) return;
        setLoadingHistoryId(entry.id);
        try {
            const freshCase = await fetchCase(entry.id);
            setIsOpen(false);
            setInputValue('');
            setDebouncedTerm('');
            onSelect(freshCase);
        } catch {
            removeSearchHistory(entry.id);
            setHistory((prev) => prev.filter((e) => e.id !== entry.id));
            showToast('Patient is no longer available', 'error');
        } finally {
            setLoadingHistoryId(null);
        }
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        // Enter is handled even before the dropdown opens: flush the debounce and
        // select the first result — otherwise Enter within the 300ms debounce (or
        // while the first page is fetching) silently did nothing.
        if (e.key === 'Enter') {
            const trimmed = inputValue.trim();
            if (trimmed.length < 2) return;
            e.preventDefault();
            if (debouncedTerm !== trimmed) {
                setDebouncedTerm(trimmed);
                setHighlightedIndex(0);
                setIsOpen(true);
                setPendingEnter(true);
            } else if (isPlaceholderData || (isFetching && results.length === 0)) {
                // Results on screen (if any) belong to the previous term — wait for
                // the current term's first page rather than selecting a stale row.
                setIsOpen(true);
                setPendingEnter(true);
            } else {
                const target = results[highlightedIndex];
                if (target) handleSelect(target);
            }
            return;
        }

        if (!isOpen) {
            if (e.key === 'Escape' && inputValue.length > 0) {
                e.preventDefault();
                setInputValue('');
                setIsOpen(false);
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = Math.min(highlightedIndex + 1, results.length - 1);
            setHighlightedIndex(next);
            // Pre-fetch next page when keyboard nav reaches the last loaded item.
            if (next === results.length - 1 && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const target = results[highlightedIndex];
            if (target) handleSelect(target);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setPendingEnter(false);
            setIsOpen(false);
            setInputValue('');
        }
    }

    // Clamp highlightedIndex to stay within bounds when the results array shrinks
    // (e.g. a new term returned fewer hits than the prior term).
    useEffect(() => {
        if (results.length > 0 && highlightedIndex >= results.length) {
            setHighlightedIndex(results.length - 1);
        }
    }, [results.length, highlightedIndex]);

    // A pending Enter resolves once the current term's first page is in (not a
    // placeholder from the previous term): select the first result. If the term
    // came back empty, just drop the flag — the "No matches" panel stays up.
    useEffect(() => {
        if (!pendingEnter) return;
        if (!isOpen) {
            setPendingEnter(false);
            return;
        }
        if (isFetching || isPlaceholderData) return;
        setPendingEnter(false);
        const target = results[0];
        if (target) handleSelect(target);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingEnter, isOpen, isFetching, isPlaceholderData, results]);

    const trimmedInput = inputValue.trim();
    const showSearchResults = isOpen && debouncedTerm.length >= 2;
    // Recent panel: focused with no query and history present. We read history
    // on focus so it always reflects what was stored by the last selection.
    const showRecentResults = isOpen && trimmedInput.length < 2 && history.length > 0;
    const showDropdown = showSearchResults || showRecentResults;

    return (
        <div ref={containerRef} className="relative flex-1 min-w-[160px] max-w-[340px] flex-shrink">
            <div className="flex items-center h-11 bg-white border border-gray-200 rounded-xl px-4">
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" />
                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-autocomplete="list"
                    aria-haspopup="listbox"
                    value={inputValue}
                    onChange={(e) => {
                        setPendingEnter(false);
                        setInputValue(e.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        const freshHistory = readSearchHistory();
                        setHistory(freshHistory);
                        if (trimmedInput.length >= 2) {
                            setIsOpen(true);
                        } else if (freshHistory.length > 0) {
                            setIsOpen(true);
                        }
                    }}
                    placeholder="Search by patient or practice name"
                    className="w-full min-w-0 bg-transparent outline-none text-gray-700 placeholder-gray-400 text-sm"
                />
                {inputValue.length > 0 && (
                    <button
                        type="button"
                        onClick={() => {
                            setInputValue('');
                            setIsOpen(false);
                        }}
                        aria-label="Clear search"
                        className="flex items-center justify-center w-5 h-5 ml-2 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors flex-shrink-0"
                    >
                        <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            {showDropdown && (
                <div
                    role="listbox"
                    aria-label={showRecentResults ? 'Recent patients' : 'Patient search results'}
                    className="absolute top-[calc(100%+6px)] left-0 right-0 z-50 bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden"
                >
                    {showRecentResults ? (
                        <div className="py-1">
                            <div className="px-3 pt-1 pb-0.5">
                                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.05em]">
                                    Recent
                                </span>
                            </div>
                            {history.map((entry) => (
                                <RecentRow
                                    key={entry.id}
                                    entry={entry}
                                    isLoading={loadingHistoryId === entry.id}
                                    onSelect={() => {
                                        void handleRecentSelect(entry);
                                    }}
                                />
                            ))}
                        </div>
                    ) : isFetching && results.length === 0 ? (
                        <div className="flex items-center justify-center gap-2 px-4 py-3 text-gray-400">
                            <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                            <span className="text-sm">Searching...</span>
                        </div>
                    ) : results.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">
                            No matches for &ldquo;{debouncedTerm}&rdquo;
                        </div>
                    ) : (
                        <div
                            ref={scrollPanelRef}
                            className="py-1 max-h-[min(420px,60vh)] overflow-y-auto"
                        >
                            {results.map((result, i) => (
                                <ResultRow
                                    key={result.id}
                                    result={result}
                                    isHighlighted={highlightedIndex === i}
                                    statusLookup={statusLookup}
                                    onMouseEnter={() => setHighlightedIndex(i)}
                                    onSelect={() => handleSelect(result)}
                                    rowRef={highlightedIndex === i ? highlightedRowRef : undefined}
                                />
                            ))}

                            {/* Sentinel: triggers fetchNextPage via IntersectionObserver */}
                            <div ref={sentinelRef} />

                            {isFetchingNextPage && (
                                <div className="flex items-center justify-center gap-2 px-4 py-2 text-gray-400">
                                    <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin flex-shrink-0" />
                                    <span className="text-sm">Loading more...</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
